import { useState, useCallback } from 'react';
import {
  useGetSocialMediaPostsQuery,
  useCreateSocialMediaPostMutation,
  useUpdateSocialMediaPostMutation,
  useDeleteSocialMediaPostMutation,
  usePostSocialMediaPostNowMutation,
} from '@/api/client';
import type {
  SocialMediaPostResponseSchema,
  CreateSocialMediaPostRequestSchema,
  UpdateSocialMediaPostRequestSchema,
} from '@/api/client';

const POLLING_INTERVAL_MS = 30000;

export function useSocialMedia(skip: boolean) {
  const [editingId, setEditingId] = useState<number | null>(null);

  const { data, isLoading, refetch } = useGetSocialMediaPostsQuery(
    { page: 1, size: 100 },
    { skip, pollingInterval: skip ? 0 : POLLING_INTERVAL_MS }
  );

  const [createMutation, { isLoading: isCreating }] = useCreateSocialMediaPostMutation();
  const [updateMutation, { isLoading: isUpdating }] = useUpdateSocialMediaPostMutation();
  const [deleteMutation, { isLoading: isDeleting }] = useDeleteSocialMediaPostMutation();
  const [postNowMutation, { isLoading: isPostingNow }] = usePostSocialMediaPostNowMutation();

  const posts: SocialMediaPostResponseSchema[] = data?.items ?? [];

  const createPost = useCallback(
    async (body: CreateSocialMediaPostRequestSchema) => {
      await createMutation(body).unwrap();
      refetch();
    },
    [createMutation, refetch]
  );

  const updatePost = useCallback(
    async (socialMediaPostId: number, body: UpdateSocialMediaPostRequestSchema) => {
      await updateMutation({
        socialMediaPostId,
        updateSocialMediaPostRequestSchema: body,
      }).unwrap();
      setEditingId(null);
      refetch();
    },
    [updateMutation, refetch]
  );

  const deletePost = useCallback(
    async (socialMediaPostId: number) => {
      await deleteMutation(socialMediaPostId).unwrap();
      refetch();
    },
    [deleteMutation, refetch]
  );

  const postNow = useCallback(
    async (socialMediaPostId: number) => {
      await postNowMutation(socialMediaPostId).unwrap();
      refetch();
    },
    [postNowMutation, refetch]
  );

  return {
    posts,
    isLoading,
    isCreating,
    isUpdating,
    isDeleting,
    isPostingNow,
    editingId,
    setEditingId,
    createPost,
    updatePost,
    deletePost,
    postNow,
  };
}
